import type { ReactNode } from 'react';
import { commissionMoney, commissionWholeMoney } from './commissionPresentation';
import { LwMetricCard, LwThemeScope } from '@/components/lw';
import type { AdjustedCommissionTotals } from './myCommissionReportModel';
import './my-commissions.css';

export function CommissionPageShell({ children }: { children: ReactNode }) {
  return <LwThemeScope className="lw-ds-page my-commissions">{children}</LwThemeScope>;
}

export function SummaryCard({ title, value, sub, tone = 'default' }: {
  title: string; value: string; sub?: string; tone?: 'default' | 'positive' | 'warning' | 'negative';
}) {
  return <LwMetricCard label={title} value={value} caption={sub} className={`mc-metric mc-tone-${tone}${value.length > 10 ? ' mc-long-value' : ''}`} />;
}

export function CommissionSummaryCards({ totals, users }: { totals: AdjustedCommissionTotals; users?: number }) {
  return <div className={`mc-metrics${users !== undefined ? ' mc-metrics-admin' : ''}`}>
    <SummaryCard title="Total Earned" value={commissionWholeMoney(totals.totalEarned)} sub={`${totals.loanCount} records`} />
    <SummaryCard title={users === undefined ? 'Cash Paid' : 'Total Paid'} value={commissionWholeMoney(totals.totalPaid)} tone="positive"
      sub={totals.totalRecaptured > 0 ? `${commissionMoney(totals.totalRecaptured)} recaptured separately` : undefined} />
    <SummaryCard title="Outstanding" value={commissionWholeMoney(totals.outstanding)} tone={totals.outstanding > 0.01 ? 'warning' : 'default'}
      sub={totals.outstanding < 0 ? 'credit' : totals.outstanding > 0.01 ? 'owed' : 'settled'} />
    <SummaryCard title="LO Profitability" value={commissionWholeMoney(totals.loProfitability)} tone={totals.loProfitability < 0 ? 'negative' : 'positive'} />
    {users !== undefined && <SummaryCard title="Users" value={String(users)} />}
    <SummaryCard title="Concessions" value={commissionWholeMoney(totals.concessionsTotal)} tone="warning" sub={`${totals.concessionFileCount} files`} />
  </div>;
}

export function CommissionAdminLedger({ totals, reconciliation }: { totals: AdjustedCommissionTotals; reconciliation: string }) {
  const clauses = [
    totals.profitabilityBonuses ? `LO Profitability includes ${commissionMoney(totals.profitabilityBonuses)} in bonuses` : '',
    totals.profitabilityVolumeBonusAdjustment ? `Volume bonus commitments / returns: ${commissionMoney(totals.profitabilityVolumeBonusAdjustment)}` : '',
    totals.profitabilityRecaptured === null ? 'Recapture activity unavailable'
      : totals.profitabilityRecaptured !== 0 ? `${commissionMoney(totals.profitabilityRecaptured)} absorbed by recapture` : '',
    reconciliation !== 'Recapture activity unavailable' ? reconciliation : ''
  ].filter(Boolean);
  return clauses.length > 0 ? <p className="mc-admin-ledger">{clauses.map((clause, index) =>
    <span key={clause}>{index > 0 && ' · '}{clause}</span>)}
  </p> : null;
}

export function CommissionReportFooter({ showSplits = false }: { showSplits?: boolean }) {
  return <footer className="mc-footer">
    <p>LO Profitability is a scorecard residual, not a payout — it does not change what is paid. Outstanding is approved commission not yet settled.</p>
    {showSplits && <p>Company-paid splits shown inside a record are not counted in the recipient’s earnings. The selected-user view mirrors that user’s own view.</p>}
    <p>Export includes the full selected period, regardless of table search, sorting, or visible columns.</p>
  </footer>;
}
